import React, { useCallback, useState, useEffect, Fragment, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Card, CardHeader, CardBody, Row, Col, Table, Input, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getVehicles, getVehicle, publish } from "../../slices/thunks";
import { enginePower, engineTypes, fuelTypes, manufacturers, models } from "../../common/data/olxCategories";
import debounce from "lodash/debounce";

import NoResults from "./NoResults";

const VehiclesTable = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = JSON.parse(sessionStorage.getItem("authUser"));
    const isAdmin = user.role == 'Admin' ? true : false; 

    const [searchValue, setSearchValue] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");
    const [selectedModel, setSelectedModel] = useState("");
    const [filteredModels, setFilteredModels] = useState([]);

    const ref = useRef();
    
    const onChange = () => {
        const request = { 
            page: 1, 
            size: 25, 
            statusFilter: 0, 
            searchInput: searchValue, 
            searchField: "",
            searchBrand: selectedBrand,
            searchModel: selectedModel
        };
        sessionStorage.setItem("vehiclePageRequest", JSON.stringify(request));
        dispatch(getVehicles(request));
    };

    useEffect(() => { ref.current = onChange; }, [onChange]);
    const debouncedCallback = useMemo(() => debounce(() => ref.current?.(), 1000), []);

    // Filter models based on selected brand
    useEffect(() => {
        if (selectedBrand && selectedBrand !== "-1") {
            const brandModels = models.filter(m => m.parentValue == selectedBrand);
            setFilteredModels(brandModels);
        } else {
            setFilteredModels(models.filter(m => m.value !== -1));
        }
    }, [selectedBrand]);

    // Trigger search when brand or model changes
    useEffect(() => {
        if (selectedBrand || selectedModel) {
            onChange();
        }
    }, [selectedBrand, selectedModel]);

    const handleBrandChange = (e) => {
        const brandValue = e.target.value;
        setSelectedBrand(brandValue);
        setSelectedModel(""); // Reset model when brand changes
    };

    const handleModelChange = (e) => {
        setSelectedModel(e.target.value);
    };

    const handleClearFilters = () => {
        setSearchValue("");
        setSelectedBrand("");
        setSelectedModel("");
        const request = { 
            page: 1, 
            size: 25, 
            statusFilter: 0, 
            searchInput: "", 
            searchField: "",
            searchBrand: "",
            searchModel: ""
        };
        sessionStorage.setItem("vehiclePageRequest", JSON.stringify(request));
        dispatch(getVehicles(request));
    };

    const storeProperties = createSelector((state) => state.Vehicle, (state) => ({
        pagedVehicles: state.pagedVehicles,
        paginationInfo: state.paginationInfo,
        pageRequest: state.pageRequest,
        error: state.error,
    }));

    const { pagedVehicles, paginationInfo, pageRequest, error } = useSelector(storeProperties);   

    const [vehicleList, setVehicleList] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    
    const handlePageChange = (page) => {
        setCurrentPage(page);
        const request = { 
            page: page, 
            size: 25, 
            statusFilter: 0, 
            searchInput: pageRequest.searchInput, 
            searchField: "",
            searchBrand: pageRequest.searchBrand || "",
            searchModel: pageRequest.searchModel || ""
        };
        sessionStorage.setItem("vehiclePageRequest", JSON.stringify(request));
        dispatch(getVehicles(request));
    };

    useEffect(() => {
        dispatch(getVehicles(pageRequest));
        setVehicleList(pagedVehicles);
    }, []);

    useEffect(() => {
        setVehicleList(pagedVehicles);
        setCurrentPage(paginationInfo.currentPage);
    }, [pagedVehicles]);
    
    const [modal, setModal] = useState(false);
    const toggleModal = useCallback(() => {
        setModal(!modal);
    }, [modal]);

    const onRowClick = (vehicle) =>{
        sessionStorage.setItem("vehicleDetails", JSON.stringify(vehicle));
        dispatch(getVehicle(vehicle.id));
        navigate("/vehicle-details");
    };
    const handlePublishClick = (vehicleId) => {
        dispatch(publish({id: vehicleId, isVehicle: true}));
    };
    const handleCopyToClipboard = async (vehicle) => {
        await navigator.clipboard.writeText("https://olx.ba/artikal/" + vehicle.listingId);
        toast.info("Link kopiran.");
    };

    return (
        <Fragment>
            <Row className="mb-3">
                <CardBody className="border border-dashed border-end-0 border-start-0">
                    <Row>
                      <Col md={3}>
                        <div className="mb-2">
                          <Input 
                            type="select" 
                            className="form-select"
                            value={selectedBrand}
                            onChange={handleBrandChange}
                          >
                            <option value="">Sve marke</option>
                            {manufacturers.filter(m => m.value !== -1).map(brand => (
                              <option key={brand.value} value={brand.value}>
                                {brand.label}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="mb-2">
                          <Input 
                            type="select" 
                            className="form-select"
                            value={selectedModel}
                            onChange={handleModelChange}
                            disabled={!selectedBrand}
                          >
                            <option value="">Svi modeli</option>
                            {filteredModels.map(model => (
                              <option key={model.value} value={model.value}>
                                {model.label}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="search-box mb-2">
                          <Input 
                            id="search" 
                            name="search" 
                            type="search" 
                            className="form-control search"
                            placeholder="ID BROJ, BROJ ŠASIJE..."
                            value={searchValue}
                            onChange={(e) => { debouncedCallback(); setSearchValue(e.target.value); }}
                          />
                          <i className="bx bx-search-alt search-icon" />
                        </div>
                      </Col>
                      <Col md={2}>
                        <Button 
                          color="secondary" 
                          outline 
                          className="w-100"
                          onClick={handleClearFilters}
                        >
                          <i className="bx bx-x me-1" />
                          Očisti
                        </Button>
                      </Col>
                    </Row>
                </CardBody>
            </Row>
            <CardBody className="pt-0">
                {(!vehicleList || !vehicleList.length) ? (<NoResults />) :
                    (<div className="table-responsive table-card mb-1">
                        <Table hover className="align-middle table-nowrap">
                            <thead className="table-light text-muted text-uppercase align-content-center">
                                <tr>
                                    <th>Id broj</th>
                                    <th>Vozilo</th>
                                    <th>Broj šasije</th>
                                    <th>Kilometraža</th>
                                    <th>Motor</th>
                                    <th>Snaga</th>
                                    <th>Oznaka</th>
                                    <th>Godište</th>
                                    <th>OLX</th>
                                </tr>
                            </thead>
                            <tbody>
                            {vehicleList.map(vehicle => (
                                <tr key={vehicle.id}>
                                    <td style={{width: 50}} onClick={() => onRowClick(vehicle)}>
                                        <h5 className="fs-14 text-uppercase text-center mb-1">{vehicle.displayId}</h5>
                                    </td>
                                    <td onClick={() => onRowClick(vehicle)}>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0 me-3">
                                                <div className="bg-light rounded overflow-hidden" style={{width: 100, height: 80}}>
                                                    <img src={vehicle.thumbnail} alt="img-thumbnail" className="d-block img-thumbnail" />
                                                </div>
                                            </div>
                                            <div>
                                                <h5 className="fs-14 mb-1">
                                                    {manufacturers.find(m => m.value == vehicle.brand).label + " " + models.find(b => b.value == vehicle.model).label}
                                                </h5>
                                            </div>
                                        </div>
                                    </td>
                                    <td onClick={() => onRowClick(vehicle)}><h5 className="fs-14 mb-1">{vehicle.vin}</h5></td>
                                    <td onClick={() => onRowClick(vehicle)}><h5 className="fs-14 mb-1">{vehicle.mileage + " KILOMETARA"}</h5></td>
                                    <td onClick={() => onRowClick(vehicle)}>
                                        <h5 className="fs-14 mb-1">{enginePower.find(item => item.value == vehicle.engine).label + " " + fuelTypes.find(f => f.value == vehicle.engineFuel).label}</h5>
                                    </td>
                                    <td onClick={() => onRowClick(vehicle)}><h5 className="fs-14 mb-1">{vehicle.enginePower + " kW"}</h5></td>
                                    <td onClick={() => onRowClick(vehicle)}><h5 className="fs-14 mb-1">{engineTypes.find(e => e.value == vehicle.engineTag).label}</h5></td>
                                    <td onClick={() => onRowClick(vehicle)}><h5 className="fs-14 mb-1">{vehicle.productionYear}</h5></td>
                                    <td>
                                    {vehicle.listingId != null && (
                                        <div className="hstack justify-content-start">
                                            <Button color='primary' outline onClick={() => {handleCopyToClipboard(vehicle)}}><i className="ri-clipboard-line fs-14" /></Button>
                                        </div>)}
                                        {(isAdmin && vehicle.listingId == null) && (
                                            <div className="hstack justify-content-start">
                                                <Button color='primary' outline onClick={() => {handlePublishClick(vehicle.id)}}>OLX</Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>)}
                    <div className="hstack gap-3 justify-content-center">
                        {currentPage <= 1 ? 
                            (<button className="page-item pagination-prev disabled">Prethodna</button>) : 
                            (<button className="page-item pagination-prev" onClick={() => handlePageChange(currentPage - 1)}>Prethodna</button>)
                        }
                        <div className="vr"></div>
                        {currentPage >= paginationInfo.totalPages ? 
                            (<button className="page-item pagination-prev disabled">Sljedeća</button>) : 
                            (<button className="page-item pagination-prev" onClick={() => handlePageChange(currentPage + 1)}>Sljedeća</button>)
                        }
                    </div>
                <h5 className="mt-4 text-center">{ "Stranica " + currentPage + " od " + paginationInfo.totalPages}</h5>
            </CardBody>            
      </Fragment>
    );
};

export default VehiclesTable;